import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors as cartSelectors } from '@elab/core/store/cart';
import { formatAmount } from '@elab/core/utils/PriceUtils';
import { debounce } from '@elab/core/utils/debounce';
import { findInObjectByPath } from '@elab/core/utils/findInObjectByPath';
import { selectors as configSelectors } from '@elab/core/store/config';
import { cacheSelector } from '@elab/core/utils/webComponent';

import { getActions, getSelectors } from './redux';

/**
 *  Component <e2-gift-card> is used to display a visual representation of a gift card
 *
 * @attr {Object} [card-number] id of the giftCard to be rendered by this component
 * @attr {Object} [remove-action] action to dispatch on remove button
 * @attr {Object} [remove-dialog-name] if remove action is to open a confirmation dialog, this is the dialog name
 * @attr {Object} [input-error-message] error message to display if user enters an invalid amount in the amount input field
 * @attr {Object} [additional-classes] classes that can be appended to <div class="gift-card"> in render function
 *
 * @attr {Object} [card-label] label before the card number
 * @attr {Object} [expire-label] label before the expiration date
 * @attr {Object} [amount-label] label before the input where the customer enters the amount to use from the gift card
 *
 * @attr {Object} cartType is the configuration object for the current card
 * @prop {string} cartTotal stores the total price of the current cart
 * @prop {string} hasError boolean to indicate if the value in the amount input field of the card is invalid
 * @prop {string} card after card info is fetched with cardNumber attribute, it is stored here
 *
 * example:
 * <e2-gift-card
 *      class="swiper-slide"
 *      card-number="${card.giftCard.cardNumber}"
 *      card-label="${this.cardNumberLabel}"
 *      expire-label="${this.expireOnLabel}"
 *      amount-label="${this.amountUseLabel}"
 *      remove-dialog-name="${this.removeDialogName}"
 *      remove-action="${this.removeAction}"
 *      input-error-message="${this.inputErrorMessage}"
 * ></e2-gift-card>
 */

export class GiftCard extends SimpleLitElement {
    static get properties() {
        return {
            card: { type: Object, attribute: false },
            cardNumber: { type: String, attribute: 'card-number' },
            cardNumberLabel: { type: String, attribute: 'card-label' },
            expireOnLabel: { type: String, attribute: 'expire-label' },
            amountUseLabel: { type: String, attribute: 'amount-label' },
            removeDialogName: {
                type: String,
                attribute: 'remove-dialog-name',
            },
            removeAction: { type: String, attribute: 'remove-action' },
            cartTotal: { type: Number, attribute: false },
            inputErrorMessage: {
                type: String,
                attribute: 'input-error-message',
            },
            additionalClasses: {
                type: String,
                attribute: 'additional-classes',
            },
            hasError: { type: Boolean, attribute: false },
            cardType: { type: String, attribute: false },
            decimalSeparator: { type: String, attribute: false },
        };
    }

    constructor() {
        super();
        this.card = {};
        this.cardNumberLabel = '';
        this.expireOnLabel = '';
        this.amountUseLabel = '';
        this.removeDialogName = '';
        this.removeAction = '';
        this.cardType = '';
        this.cartTotal = 0;
        this.inputErrorMessage = '';
        this.hasError = false;
        this.additionalClasses = '';
        this.decimalSeparator = '';
    }

    handleAmountChange({ target: { value } }) {
        this.dispatchEvent(
            new CustomEvent('update-gift-card-amount', {
                detail: {
                    cardNumber: this.cardNumber,
                    amountToUse: value.replace(this.decimalSeparator, '.'),
                    cartTotal: this.cartTotal,
                    cardBalance: this.card.giftCard.balance.value,
                },
            }),
        );
    }

    getInputClassMap() {
        return classMap({
            'gift-card__input--error': this.hasError,
        });
    }

    getErrorClassMap() {
        return classMap({
            'gift-card__input-error--visible': this.hasError,
        });
    }

    getExpirationMessage(expirationMessage) {
        return expirationMessage && expirationMessage.trim().length !== 0
            ? html`
                  <span class="info-icon"></span>${expirationMessage}
              `
            : '';
    }

    render() {
        return html`
            <div class="gift-card ${this.additionalClasses} ${this.cardType}">
                <div class="gift-card__container">
                    <div class="gift-card__content">
                        <span class="gift-card__expiration">
                            ${this.getExpirationMessage(
                                this.card.giftCard.expirationIntervalMessage,
                            )}
                        </span>
                        <div class="gift-card__card">
                            <div class="gift-card__data">
                                <div class="gift-card__amount">
                                    ${this.card.giftCard.balance.formattedValue}
                                </div>

                                <div class="gift-card__info">
                                    <p class="gift-card__number">
                                        <span class="gift-card__label">
                                            ${this.cardNumberLabel}
                                        </span>
                                        ${this.card.giftCard.cardNumber}
                                    </p>

                                    <p class="gift-card__date">
                                        <span class="gift-card__label">
                                            ${this.expireOnLabel}
                                        </span>
                                        ${this.card.giftCard.expiryDate}
                                    </p>
                                </div>
                            </div>
                            <div class="gift-card__amount-to-use">
                                <label>
                                    <p class="gift-card__amount-label">
                                        ${this.amountUseLabel}
                                    </p>
                                    <input
                                        class="gift-card__input ${this.getInputClassMap()}"
                                        @input="${debounce(
                                            this.handleAmountChange.bind(this),
                                            1000,
                                        )}"
                                        value="${String(
                                            formatAmount(this.card.appliedAmount.value, 2),
                                        ).replace('.', this.decimalSeparator)}"
                                    />
                                </label>
                            </div>

                            <e2-action-button
                                class-modifier="button--remove-gift-card ${this.cardType}"
                                class="gift-card__remove"
                                action-type="${this.removeAction}"
                                data-number="${this.card.giftCard.cardNumber}"
                                data-dialog-name="${this.removeDialogName}"
                            ></e2-action-button>
                        </div>
                    </div>

                    <div class="gift-card__input-error ${this.getErrorClassMap()}">
                        <span class="input-error__icon"></span>
                        <span class="input-error__text">${this.inputErrorMessage}</span>
                    </div>
                </div>
            </div>
        `;
    }
}

const mapStateToProps = (state, ctx) => {
    const { cardNumber } = ctx;
    const card = cartSelectors.getGiftCardTransactionByNumber(state, cardNumber);

    return {
        card,
        cartTotal: cartSelectors.getTotalPrice(state),
        cardType: findInObjectByPath(card, ['giftCard', 'type'], 'postilion').toLowerCase(),
        hasError: cacheSelector(ctx, getSelectors, 'gift-card').getCardHasError(state, cardNumber),
        decimalSeparator: configSelectors.getGiftCardAmountDecimalSeparator(state),
    };
};

const mapDispatchToEvents = dispatch => ({
    'update-gift-card-amount': ({ detail }) => {
        dispatch(getActions('gift-card').updateGiftCardAmount(detail));
    },
});

export default () =>
    customElements.define('e2-gift-card', connect(mapStateToProps, mapDispatchToEvents)(GiftCard));
